import React from 'react'
import { ValidationError } from 'yup'
import { useDispatch } from 'react-redux'

import { TAllBasedFieldsNames, TAllFormNames, FormNames } from '../../../models/form/IFormDefinition'
import { BasicInfoFieldsNames } from '../../../formDefinitions/BasicInfo/BasicInfoFields'
import { store } from '../../../store'
import { CLEAR_FIELD_VALIDATION_ERROR, SET_SINGLE_FIELD_VALIDATION_ERROR } from '../../../store/actions/form-actions'
import { Environment } from '../../../environment/app-settings'
import { IHandleValidateFormProps } from '../FormProps'
import { IFieldsDataStateFieldValue } from '../../../store/types'


const useFormLogic = () => {
   const dispatch = useDispatch()

   /**
    * Funkcja pomocnicza - Pobiera pierwszą kontrolkę z klasą błędu z formularza i scrolluje widok do niej 
   */
   const scrollToErrorInput = () => {
      const scrollToErrorInput: Element = document.getElementsByClassName(Environment.fieldErrorClassName)[0]
      
      if(scrollToErrorInput !== undefined) {
         scrollToErrorInput.scrollIntoView({ behavior:"smooth", block: "center" })
      }
   }
    
   /**
    * Funkcja pozwalajaca na walidację pojedynczego pola. Obsługuje również customowe walidacje dla pól zależnych od innych pól 
   */
   const handleValidateSingleField = React.useCallback((formName: TAllFormNames, fieldName: Partial<TAllBasedFieldsNames>, fieldValue: IFieldsDataStateFieldValue, fieldValidationFunction: any): boolean => {
      
      switch(fieldName){
         case BasicInfoFieldsNames.ConfirmEmail: { // Specjalna customowa walidacja, potrzebująca dodatkowych parametrów i specjalnej obsługi.
            try {
               const currentState = store.getState()
               const emailFieldValue = currentState.formState[FormNames.BasicInfoForm].fieldsData[BasicInfoFieldsNames.Email].value                

               fieldValidationFunction(fieldValue, emailFieldValue)
               
               dispatch({
                  type: CLEAR_FIELD_VALIDATION_ERROR,
                  payload: { formName, fieldName }
               })

               return true // Walidacja przeszła
            }
            catch (error: any) {
               if (error instanceof ValidationError) {

                  dispatch({
                     type: SET_SINGLE_FIELD_VALIDATION_ERROR,
                     payload: { formName, fieldName, newValidationError: error.message }
                  })

                  return false // Walidacja nie przeszła
               }
            }

            break;
         }
         default: {
            try {
               if(fieldValidationFunction !== undefined) {
                  fieldValidationFunction.validateSync(fieldValue)
                  
                  dispatch({
                     type: CLEAR_FIELD_VALIDATION_ERROR,
                     payload: { formName, fieldName }
                  })
   
                  return true // Walidacja przeszła
               }
            } 
            catch (error) {
               if (error instanceof ValidationError) {
                  dispatch({
                     type: SET_SINGLE_FIELD_VALIDATION_ERROR,
                     payload: { formName, fieldName, newValidationError: error.message }
                  })

                  return false // Walidacja nie przeszła
               }
               else {
                  console.trace(`Nieobsługiwany błąd walidacji: ${error}`)
                  return true // Dostaliśmy błąd inny niż walidacyjny. Z racji że nie można w tym przypadku ustalić teksu błędu, zwracamy true aby nie blokować formularza i nie wprowadzać użytkownika w błąd.
               }
            }
         }
      }

      return false
   }, [])


   /**
    * Funkcja walidująca wszystkie kontrolki w formularzu.
   */
   const handleValidateForm = React.useCallback(({ formName, fieldValuesObject, formValidationFunctionsObject }: IHandleValidateFormProps): boolean => {
      const validationResults = []      
      
      for (const [fieldName, fieldValue] of Object.entries(fieldValuesObject) as [keyof typeof fieldValuesObject, typeof fieldValuesObject[keyof typeof fieldValuesObject]][]) {
         
         if(formValidationFunctionsObject[fieldName] !== undefined){
            
            const isCurrentFieldValid = handleValidateSingleField(formName, fieldName as Partial<TAllBasedFieldsNames>, fieldValue, formValidationFunctionsObject[fieldName])
               validationResults.push(isCurrentFieldValid)
               const currentInputWrapperElement: Element | null = document.querySelector(`.${Environment.fieldBaseClassName}:has(#${fieldName})`)

               if(isCurrentFieldValid) { // Jeśli nie mamy błędu walidacji, dla pewności usuwamy klasę z błędem, niezależnie od tego czy istnieje czy nie
                  currentInputWrapperElement?.classList.remove(Environment.fieldErrorClassName)
               }
               else { // Jeśli mamy błąd walidacji a kontrolka nie posiada jeszcze klasy z błędem, dodajemy ją
                  !currentInputWrapperElement?.classList.contains(Environment.fieldErrorClassName) && currentInputWrapperElement?.classList.add(Environment.fieldErrorClassName)
               }
         }
         else {
            validationResults.push(true) // Nie mamy błędu bo nie ma walidacji na polu

            dispatch({
               type: CLEAR_FIELD_VALIDATION_ERROR,
               payload: { formName, fieldName }
            })
         }
      }

      const isAllValidationsPassed = validationResults.every((validationResult: boolean) => validationResult === true)
      !isAllValidationsPassed && scrollToErrorInput()

      return isAllValidationsPassed // Jeśli true - formularz przeszedł walidację i dane są poprawne

   }, [])


   return {
      handleValidateForm,
      handleValidateSingleField,
   }
}

export default useFormLogic